import * as Types from '@aeppic/types'

import uuid from '@aeppic/shared/uuid'

import IGenericResource from './resource'
import { Context } from './contextify'
import { AeppicInterface } from './aeppic.js'

type ImageDerivativeFit = 'cover' | 'contain' | 'fill' | 'inside' | 'outside'
type ImageDerivativeFormat = 'pdf' | 'jpg' | 'jpeg' | 'png' | 'webp'
type ImageDerivativeBackground = 'transparent' | 'black' | 'white' | string
type ImageDerivativeRotation = 0 | 90 | 180 | 270 | -90 | -180 | -270

type ImageDerivativeOptions = {
  width?: string | number
  height?: string | number
  maxWidth?: string | number
  maxHeight?: string | number
  minWidth?: string | number
  minHeight?: string | number
  square?: boolean
  aspectRatio?: string | number
  fit?: ImageDerivativeFit
  background?: ImageDerivativeBackground
  position?: string
  rotate?: ImageDerivativeRotation | number
  autoRotate?: boolean
  flip?: boolean
  flop?: boolean
  format?: ImageDerivativeFormat
  quality?: string | number
  noCache?: boolean
  ignoreStoredUrl?: boolean
  forceGenerate?: boolean
  preferLocal?: boolean
  offline?: boolean
}

type ThumbnailOptions = ImageDerivativeOptions

type PreviewOptions = ImageDerivativeOptions & {
  downloadFileName?: string
  firstPage?: number
  lastPage?: number
}

/**
 * A file to hand to a service: a document (or its id) and a file field.
 * `version` defaults to the document object's `v`; pass `false` to bind to the
 * server's current version instead.
 */
export type DownloadUrlTarget = {
  document: Types.Document | Types.Reference | string
  fieldName: string
  version?: string | false
}

export type DownloadUrlOptions = {
  /** Bind to this version; defaults to `document.v`. `false` binds to the current server version. */
  version?: string | false
  /** Return an absolute URL (default) or the server-relative path (recommended for proxied services). */
  absolute?: boolean
}

/** Server response for one issued download token. */
export type DownloadTokenInfo = {
  documentId: string
  fieldName: string
  version: string
  file: { name?: string, type?: string, size?: number }
  expiresAt: string
  downloadPath: string
  downloadUrl: string
}

export class Content implements IGenericResource {
  private _objectUrlCache: Map<string, any> = new Map()
  private _imageDerivativeCache: Map<string, string> = new Map()
  private _pendingImageDerivatives: Map<string, Promise<string>> = new Map()
  
  constructor(private _aeppic: AeppicInterface, private _apiPrefix: string, private _urlToDataCache:  Map<string, any>) {}

  getSrc(document: Types.Document, fieldName: string, options: { downloadFileName?: string, noCache?: boolean } = {}): string {
    return this._getSrcFromDocument(document, fieldName, 'dataUrl', options)
  }

  _getSrcFromDocument(document: Types.Document, fieldName: string, urlName, { downloadFileName, noCache }: { downloadFileName?: string, noCache?: boolean } = {}): string {
    if (typeof arguments[3] === 'string') {
      console.error('getSrc signature has changed')
    }

    const dataUrl = this._url(document, fieldName, urlName)

    if ('string' === typeof dataUrl) {
      if (this._isContentUrl(dataUrl)) {
        const cachedContent = this._objectUrlCache.get(dataUrl)
        const dataCache = this._urlToDataCache.get(dataUrl)

        const fieldType = this._fieldType(document, fieldName)

        if (!noCache && dataCache && 'string' === typeof fieldType && (fieldType.startsWith('image') || fieldType.startsWith('application/pdf'))) {
          if ('string' === typeof cachedContent && (cachedContent.startsWith('data:') || cachedContent.startsWith('blob:'))) {
            return cachedContent
          }

          if ('string' === typeof dataCache && (dataCache.startsWith('data:') || dataCache.startsWith('blob:'))) {
            this._objectUrlCache.set(dataUrl, dataCache)
            return dataCache
          }

          const objectURL = URL.createObjectURL(dataCache)
          this._objectUrlCache.set(dataUrl, objectURL)
          return objectURL
        }

        return this._getUrl(document, fieldName, urlName, { noCache, downloadFileName })
      }

      return dataUrl
    }
  }
  getUrl(document: Types.Document, fieldName: string, options: { downloadFileName?: string, noCache?: boolean } = {}): string {
    return this._getUrl(document, fieldName, 'dataUrl', options )
  }

  _getUrl(document: Types.Document, fieldName: string, urlName: string, { noCache, downloadFileName }: { downloadFileName?: string, noCache?: boolean } = {}): string {
    const url = this._url(document, fieldName, urlName)
    const fileName = downloadFileName || this._fileName(document, fieldName)

    if ('string' === typeof url) {
      if (this._isContentUrl(url)) {
        return `${this._apiPrefix}/docs/${document.id}/fields/${fieldName}/_content${fileName ?  '/' + encodeURIComponent(fileName) : '' }?${this._urlAsQueryParam(url)}${noCache ? '&cache_burst=' + uuid() : ''}`
      }
    }
  }

  _isContentUrl(url: string): boolean {
    if (url.startsWith('aeppic') || url.startsWith('store://')) {
      return true
    }

    return !url.startsWith('data:')
      && !url.startsWith('blob:')
      && !url.startsWith('http://')
      && !url.startsWith('https://')
      && !url.startsWith('/')
      && !url.startsWith('#')
  }

  getThumbnailSrc(document: Types.Document, fieldName: string, options: ThumbnailOptions = {}): string {
    const normalizedOptions = this._normalizeThumbnailOptions(options)
    const thumbnailUrl = this._getStoredDerivativeSrc(document, fieldName, 'thumbnailUrl', normalizedOptions)

    if (thumbnailUrl) {
      return thumbnailUrl
    }

    const localThumbnailSrc = this._getLocalImageDerivativeSrc(document, fieldName, 'thumbnail', normalizedOptions)

    if (localThumbnailSrc) {
      return localThumbnailSrc
    }

    const type = this._fieldType(document, fieldName)

    if (!type) {
      return null
    }

    const dataUrl = this._dataUrl(document, fieldName)

    return `${this._apiPrefix}/services/5af240ae-f20f-4554-becd-6a83c8e7ec52/thumbnail/${document.id}?${this._buildQuery({
      version: document.v,
      fieldName,
      dataUrl,
      ...this._imageDerivativeQuery(normalizedOptions)
    })}`
  }

  getIconSrc(document: Types.Document, fieldName?: string, options: { noCache?: boolean } = {}): string {
    if (fieldName) {
      const fieldIconSrc = this._getSrcFromDocument(document, fieldName, 'iconUrl', options)

      if (fieldIconSrc) {
        return fieldIconSrc
      }
    }

    const documentIconSrc = this._getIconFieldSrc(document, options)

    if (documentIconSrc) {
      return documentIconSrc
    }

    const formDocument = this._getFormDocument(document)
    const formIconSrc = this._getIconFieldSrc(formDocument, options)

    if (formIconSrc) {
      return formIconSrc
    }

    return null
  }

  getPreviewSrc(document: Types.Document, fieldName: string, options: PreviewOptions = { format: 'pdf' } ) {
    const normalizedOptions = this._normalizePreviewOptions(options)
    const previewUrl = this._getStoredDerivativeSrc(document, fieldName, 'previewUrl', normalizedOptions)

    if (previewUrl) {
      return previewUrl
    }

    const localPreviewSrc = this._getLocalImageDerivativeSrc(document, fieldName, 'preview', normalizedOptions)

    if (localPreviewSrc) {
      return localPreviewSrc
    }

    const fieldType = this._fieldType(document, fieldName)
    const expectedType = normalizedOptions.format === 'pdf' ? 'application/pdf' : 'image/'

    if (typeof fieldType === 'string' && fieldType.startsWith(expectedType) && !this._hasImageDerivativeOptions(normalizedOptions)) {
      return this.getSrc(document, fieldName, { downloadFileName: normalizedOptions.downloadFileName })
    }
    
    return this._converterUrl(document, fieldName, normalizedOptions.format, normalizedOptions)
  }

  getPdfUrl(document: Types.Document, fieldName: string, options: { downloadFileName?: string, firstPage?: number, lastPage?: number } = {}) {
    const downloadFileName = typeof options === 'string' ? options : options.downloadFileName

    if (typeof options === 'string') {
      console.error('getPdfUrl signature has changed')
    }

    const fieldType = this._fieldType(document, fieldName)

    if (typeof fieldType === 'string' && fieldType.startsWith('application/pdf')) {
      return this.getSrc(document, fieldName, { downloadFileName })
    }
    
    return this._converterUrl(document, fieldName, 'pdf', { downloadFileName, firstPage: options.firstPage, lastPage: options.lastPage })
  }

  /**
   * Create a single-use, short-lived download URL for one file field that works
   * without cookies. The server checks the current user's read access, binds the
   * URL to document, field and version, and invalidates it on first use. It never
   * grants write access.
   *
   * For services behind `/api/services/<service-id>/…` pass `{ absolute: false }`:
   * the service resolves the relative path against the `X-Aeppic-Api-Url` header
   * Aeppic's proxy sends, so it reaches Aeppic by its internal address.
   * Absolute URLs (the default) use this client's API origin.
   */
  async createDownloadUrl(document: DownloadUrlTarget['document'], fieldName: string, options: DownloadUrlOptions = {}): Promise<string> {
    const [info] = await this.createDownloadTokens([{ document, fieldName, version: options.version }])
    return this._downloadUrlFromInfo(info, options.absolute)
  }

  /** Batch variant of {@link createDownloadUrl}: one request, URLs in input order, all or nothing. */
  async createDownloadUrls(targets: DownloadUrlTarget[], options: Pick<DownloadUrlOptions, 'absolute'> = {}): Promise<string[]> {
    const infos = await this.createDownloadTokens(targets)
    return infos.map(info => this._downloadUrlFromInfo(info, options.absolute))
  }

  /** Issue download tokens and return the full server responses (file name, version, expiry, paths). */
  async createDownloadTokens(targets: DownloadUrlTarget[]): Promise<DownloadTokenInfo[]> {
    if (!Array.isArray(targets) || targets.length === 0) {
      return []
    }
    const files = targets.map(({ document, fieldName, version }) => {
      const id = typeof document === 'string' ? document : document?.id
      if (!id || !fieldName) {
        throw new Error('createDownloadUrl requires a document (or id) and a field name')
      }
      const boundVersion = version === false ? undefined
        : version ?? (typeof document === 'object' && typeof (document as any).v === 'string' ? (document as any).v : undefined)
      return boundVersion ? { id, field: fieldName, version: boundVersion } : { id, field: fieldName }
    })

    const single = files.length === 1
    const url = single
      ? `${this._apiPrefix}/docs/${encodeURIComponent(files[0].id)}/fields/${encodeURIComponent(files[0].field)}/_download-token`
      : `${this._apiPrefix}/docs/_download-tokens`
    const body = single ? (files[0].version ? { version: files[0].version } : {}) : { files }

    const response = await this._aeppic.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
    let payload: any = null
    try {
      payload = await response.json()
    } catch (_) {
      payload = null
    }
    if (!response.ok) {
      const error: any = new Error(payload?.error || `Could not create download URL (HTTP ${response.status})`)
      error.status = response.status
      throw error
    }
    return single ? [payload] : payload.tokens
  }

  _downloadUrlFromInfo(info: DownloadTokenInfo, absolute = true): string {
    if (!absolute) {
      return info.downloadPath
    }
    const origin = this._apiOrigin()
    return origin ? new URL(info.downloadPath, origin).toString() : info.downloadUrl
  }

  /** Origin of the API: from an absolute API prefix, else the page's origin. */
  _apiOrigin(): string | null {
    if (/^https?:\/\//i.test(this._apiPrefix)) {
      return new URL(this._apiPrefix).origin
    }
    const location = (globalThis as any).location
    if (location && typeof location.origin === 'string' && /^https?:/i.test(location.origin)) {
      return location.origin
    }
    return null
  }

  async fetch(document: Types.Document, fieldName: string) {
    const url = this.getSrc(document, fieldName)
    return this._aeppic.fetch(url)
  }

  async getStream(document: Types.Document, fieldName: string) {
    const response = await this.fetch(document, fieldName)

    if (response.ok) {
      return response.body
    }
  }

  async getBuffer(document: Types.Document, fieldName: string) {
    const response = await this.fetch(document, fieldName)

    if (response.ok) {
      if ('arrayBuffer' in response) {
        return response.arrayBuffer()
      } else if ('buffer' in response) {
        return (response as any).buffer()
      } else {
        throw new Error('Response does not have arrayBuffer or buffer method')
      }
    }
  }
  
  _converterUrl(document: Types.Document, fieldName: string, toFormat: ImageDerivativeFormat, options: PreviewOptions = {}) {
    const path = `/docs/${document.id}/fields/${fieldName}/convert${options.downloadFileName ? '/' + encodeURIComponent(options.downloadFileName) : ''}`

    return `${this._apiPrefix}${path}?${this._buildQuery({
      dataUrl: this._dataUrl(document, fieldName),
      version: document.v,
      format: toFormat,
      firstPage: options.firstPage,
      lastPage: options.lastPage,
      ...this._imageDerivativeQuery(options)
    })}`
  }

  _dataUrl(document: Types.Document, fieldName: string) {
    return this._url(document, fieldName, 'dataUrl')
  }

  _url(document: Types.Document, fieldName: string, urlName: string) {
    const field: any = document && document.data && document.data[fieldName]

    if (field && urlName in field) {
      return field[urlName]
    }

    return null
  }
  
  _urlAsQueryParam(url: string|null): string {
    if (typeof url === 'string') {
      return `dataUrl=${encodeURIComponent(url)}`
    }
    return ''
  }
  
  _fieldType(document: Types.Document, fieldName: string) {
    const field = <any>document.data[fieldName]

    if (field && 'type' in field) {
      return field.type
    }
    return null
  }

  _getIconFieldSrc(document: Types.Document, options: { noCache?: boolean } = {}): string {
    if (!document || !document.data || !document.data.icon) {
      return null
    }

    return this._getSrcFromDocument(document, 'icon', 'iconUrl', options)
      || this._getSrcFromDocument(document, 'icon', 'thumbnailUrl', options)
      || this._getSrcFromDocument(document, 'icon', 'dataUrl', options)
  }

  _getFormDocument(document: Types.Document): Types.Document {
    if (!document || !(this._aeppic as any).getDocumentForm) {
      return null
    }

    const form = (this._aeppic as any).getDocumentForm(document)

    return form && form.document ? form.document : null
  }

  _normalizeThumbnailOptions(options: ThumbnailOptions): ThumbnailOptions {
    const normalizedOptions = options || {}

    return {
      fit: normalizedOptions.fit || (normalizedOptions.square || normalizedOptions.aspectRatio ? 'cover' : 'inside'),
      autoRotate: normalizedOptions.autoRotate !== false,
      ...normalizedOptions
    }
  }

  _normalizePreviewOptions(options: PreviewOptions): PreviewOptions {
    const normalizedOptions = options || {}

    return {
      format: 'pdf',
      autoRotate: normalizedOptions.autoRotate !== false,
      ...normalizedOptions
    }
  }

  _getStoredDerivativeSrc(document: Types.Document, fieldName: string, urlName: string, options: ImageDerivativeOptions & { downloadFileName?: string }): string {
    if (options.ignoreStoredUrl || options.forceGenerate) {
      return null
    }

    return this._getSrcFromDocument(document, fieldName, urlName, { noCache: options.noCache, downloadFileName: options.downloadFileName })
  }

  _fileName(document: Types.Document, fieldName: string): string {
    const field: any = document && document.data && document.data[fieldName]

    return typeof field?.name === 'string' && field.name.trim() ? field.name.trim() : null
  }

  _getLocalImageDerivativeSrc(document: Types.Document, fieldName: string, variant: 'thumbnail' | 'preview', options: ImageDerivativeOptions): string {
    if (!this._shouldUseLocalDerivative(document, fieldName, options)) {
      return null
    }

    const key = this._imageDerivativeCacheKey(document, fieldName, variant, options)
    const cachedSrc = this._imageDerivativeCache.get(key)

    if (cachedSrc) {
      return cachedSrc
    }

    this.createLocalImageDerivativeSrc(document, fieldName, options, variant).catch(error => {
      console.warn('Could not create local image derivative', error)
    })

    return this.getSrc(document, fieldName, { noCache: options.noCache })
  }

  async createLocalImageDerivativeSrc(document: Types.Document, fieldName: string, options: ImageDerivativeOptions = {}, variant: 'thumbnail' | 'preview' = 'thumbnail'): Promise<string> {
    const normalizedOptions = variant === 'thumbnail' ? this._normalizeThumbnailOptions(options) : this._normalizePreviewOptions(options as PreviewOptions)
    const key = this._imageDerivativeCacheKey(document, fieldName, variant, normalizedOptions)
    const cachedSrc = this._imageDerivativeCache.get(key)

    if (cachedSrc) {
      return cachedSrc
    }

    const pending = this._pendingImageDerivatives.get(key)

    if (pending) {
      return pending
    }

    const promise = this._createLocalImageDerivativeSrc(document, fieldName, normalizedOptions).then(src => {
      this._imageDerivativeCache.set(key, src)
      this._pendingImageDerivatives.delete(key)
      return src
    }, error => {
      this._pendingImageDerivatives.delete(key)
      throw error
    })

    this._pendingImageDerivatives.set(key, promise)
    return promise
  }

  async _createLocalImageDerivativeSrc(sourceDocument: Types.Document, fieldName: string, options: ImageDerivativeOptions): Promise<string> {
    const dataUrl = this._dataUrl(sourceDocument, fieldName)
    const content = this._urlToDataCache.get(dataUrl)
    const src = await this._contentToImageSrc(content)
    const image = await this._loadImage(src)
    const dimensions = this._resolveDerivativeDimensions(image.naturalWidth || image.width, image.naturalHeight || image.height, options)
    const canvas = sourceDocument ? window.document.createElement('canvas') : null

    if (!canvas) {
      return src
    }

    canvas.width = dimensions.width
    canvas.height = dimensions.height

    const context = canvas.getContext('2d')

    if (!context) {
      return src
    }

    if (options.background && options.background !== 'transparent') {
      context.fillStyle = this._normalizeBackground(options.background)
      context.fillRect(0, 0, canvas.width, canvas.height)
    }

    const rotate = this._normalizeRotation(options.rotate)
    const draw = this._resolveDrawBox(image.naturalWidth || image.width, image.naturalHeight || image.height, dimensions.width, dimensions.height, options.fit || 'inside')

    context.save()
    context.translate(canvas.width / 2, canvas.height / 2)

    if (rotate) {
      context.rotate((rotate * Math.PI) / 180)
    }

    context.scale(options.flop ? -1 : 1, options.flip ? -1 : 1)
    context.drawImage(image, draw.x - canvas.width / 2, draw.y - canvas.height / 2, draw.width, draw.height)
    context.restore()

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(result => {
        if (!result) {
          reject(new Error('Could not create local image derivative blob'))
          return
        }

        resolve(result)
      }, this._canvasMimeType(options.format), this._canvasQuality(options.quality))
    })

    return URL.createObjectURL(blob)
  }

  _shouldUseLocalDerivative(document: Types.Document, fieldName: string, options: ImageDerivativeOptions): boolean {
    const fieldType = this._fieldType(document, fieldName)
    const dataUrl = this._dataUrl(document, fieldName)
    const hasCachedContent = dataUrl && this._urlToDataCache.has(dataUrl)
    const offline = options.offline === true || (typeof navigator !== 'undefined' && navigator.onLine === false)

    return hasCachedContent && typeof fieldType === 'string' && fieldType.startsWith('image/') && (offline || options.preferLocal)
  }

  _contentToImageSrc(content: any): Promise<string> {
    if (typeof content === 'string') {
      return Promise.resolve(content)
    }

    if (typeof Blob !== 'undefined' && content instanceof Blob) {
      return Promise.resolve(URL.createObjectURL(content))
    }

    if (typeof File !== 'undefined' && content instanceof File) {
      return Promise.resolve(URL.createObjectURL(content))
    }

    throw new Error('Cached content is not image-compatible')
  }

  _loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image()
      image.onload = () => resolve(image)
      image.onerror = reject
      image.src = src
    })
  }

  _resolveDerivativeDimensions(sourceWidth: number, sourceHeight: number, options: ImageDerivativeOptions) {
    const requestedWidth = this._numericOption(options.width)
    const requestedHeight = this._numericOption(options.height)
    const maxWidth = this._numericOption(options.maxWidth)
    const maxHeight = this._numericOption(options.maxHeight)
    const aspectRatio = options.square ? 1 : this._aspectRatio(options.aspectRatio)

    let width = requestedWidth || maxWidth || (requestedHeight && aspectRatio ? requestedHeight * aspectRatio : null) || sourceWidth
    let height = requestedHeight || maxHeight || (requestedWidth && aspectRatio ? requestedWidth / aspectRatio : null) || sourceHeight

    if (aspectRatio && !requestedWidth && !requestedHeight) {
      if (sourceWidth / sourceHeight > aspectRatio) {
        width = height * aspectRatio
      } else {
        height = width / aspectRatio
      }
    }

    if (maxWidth && width > maxWidth) {
      height *= maxWidth / width
      width = maxWidth
    }

    if (maxHeight && height > maxHeight) {
      width *= maxHeight / height
      height = maxHeight
    }

    return {
      width: Math.max(1, Math.round(width)),
      height: Math.max(1, Math.round(height))
    }
  }

  _resolveDrawBox(sourceWidth: number, sourceHeight: number, targetWidth: number, targetHeight: number, fit: ImageDerivativeFit) {
    if (fit === 'fill') {
      return { x: 0, y: 0, width: targetWidth, height: targetHeight }
    }

    const scale = fit === 'cover' ? Math.max(targetWidth / sourceWidth, targetHeight / sourceHeight) : Math.min(targetWidth / sourceWidth, targetHeight / sourceHeight)
    const width = sourceWidth * scale
    const height = sourceHeight * scale

    return {
      x: (targetWidth - width) / 2,
      y: (targetHeight - height) / 2,
      width,
      height
    }
  }

  _imageDerivativeQuery(options: ImageDerivativeOptions) {
    return {
      width: options.width,
      height: options.height,
      maxWidth: options.maxWidth,
      maxHeight: options.maxHeight,
      minWidth: options.minWidth,
      minHeight: options.minHeight,
      square: options.square,
      aspectRatio: options.aspectRatio,
      fit: options.fit,
      background: options.background,
      position: options.position,
      rotate: options.rotate,
      autoRotate: options.autoRotate,
      flip: options.flip,
      flop: options.flop,
      format: options.format,
      quality: options.quality
    }
  }

  _hasImageDerivativeOptions(options: ImageDerivativeOptions) {
    return Object.values(this._imageDerivativeQuery(options)).some(value => value !== undefined && value !== null && value !== '')
  }

  _buildQuery(values: Record<string, any>) {
    const params = new URLSearchParams()

    for (const [key, value] of Object.entries(values)) {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value))
      }
    }

    return params.toString()
  }

  _imageDerivativeCacheKey(document: Types.Document, fieldName: string, variant: string, options: ImageDerivativeOptions) {
    return `${document.id}@${document.v}/${fieldName}/${variant}/${JSON.stringify(this._imageDerivativeQuery(options))}`
  }

  _numericOption(value: string | number) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value
    }

    if (typeof value === 'string' && /^\d+(\.\d+)?$/.test(value)) {
      return Number(value)
    }

    return null
  }

  _aspectRatio(value: string | number) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return value
    }

    if (typeof value === 'string') {
      const parts = value.split(':')

      if (parts.length === 2) {
        const width = Number(parts[0])
        const height = Number(parts[1])

        if (width > 0 && height > 0) {
          return width / height
        }
      }

      const numericValue = Number(value)

      if (numericValue > 0) {
        return numericValue
      }
    }

    return null
  }

  _normalizeRotation(value: number) {
    if (!value) {
      return 0
    }

    return ((value % 360) + 360) % 360
  }

  _normalizeBackground(value: ImageDerivativeBackground) {
    if (value === 'black') {
      return '#000'
    }

    if (value === 'white') {
      return '#fff'
    }

    return value || 'transparent'
  }

  _canvasMimeType(format: ImageDerivativeFormat) {
    if (format === 'jpg' || format === 'jpeg') {
      return 'image/jpeg'
    }

    if (format === 'webp') {
      return 'image/webp'
    }

    return 'image/png'
  }

  _canvasQuality(value: string | number) {
    const quality = this._numericOption(value)

    if (!quality) {
      return undefined
    }

    return quality > 1 ? quality / 100 : quality
  }

  release() {}
}
